/**
 * TemplateVersionsPage — /agents/templates/:templateId/versions
 * Version history of one template on the DetailPage template. Demo page: /billing-and-plan (stacked cards: a setting
 * row, then the history). Promote / rollback / test / diff keep their behaviour; only the data layer and chrome moved.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitBranch, GitCompare, Plus } from 'lucide-react';
import {
  createAgentTemplateVersionMutation,
  diffAgentTemplateVersionsOptions,
  getSubjectEvaluationsOptions,
  getSubjectEvaluationsQueryKey,
  getTemplateOptions,
  getTemplateQueryKey,
  listAgentTemplateVersionsOptions,
  listAgentTemplateVersionsQueryKey,
  promoteAgentTemplateVersionMutation,
  rollbackAgentTemplateVersionMutation,
  setTemplateAutoUpgradeMutation,
  testAgentTemplateVersionMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { zAgentTemplateVersionCreate } from '../../generated/api/zod.gen.js';
import type { AgentTemplateVersionDiff, SubjectEvaluations } from '../../generated/api/types.gen.js';
import {
  Button,
  Callout,
  EmptyState,
  Modal,
  QueryBoundary,
  SettingList,
  SettingRow,
  Skeleton,
  Toggle,
  useToast,
} from '../../components/ui/index.js';
import { DetailPage } from '../../components/page/index.js';
import { Form, FormActions, TextAreaFormField, useZodForm } from '../../components/form/index.js';
import { Can } from '../../components/auth/index.js';
import { VersionCard, DiffPanel, type TestVerdict } from '../../components/agents/VersionCard.js';
import { VersionComparePanel } from '../../components/agents/VersionComparePanel.js';

// Bounds the post-test auto-poll. A slow local-LLM eval-gate run may still be in flight when this elapses, so the
// timed-out state says "may still be running" (offer a re-check) — it never implies the gate failed.
const TEST_POLL_TIMEOUT_MS = 10 * 60 * 1000;

function computeVerdict(evals: SubjectEvaluations | null): 'passed' | 'failed' | null {
  if (!evals || evals.assignments.length === 0) return null;
  const allDone = evals.assignments.every((a) => a.promotionVerdict.reason !== 'not_evaluated');
  if (!allDone) return null;
  return evals.assignments.every((a) => a.promotionVerdict.satisfied) ? 'passed' : 'failed';
}

const createVersionSchema = zAgentTemplateVersionCreate.pick({ changeSummary: true });

export const TemplateVersionsPage: React.FC = () => {
  const { templateId = '' } = useParams<{ templateId: string }>();
  const queryClient = useQueryClient();
  const { notify } = useToast();

  const [showCreate, setShowCreate] = useState(false);
  const [promoteError, setPromoteError] = useState<string | null>(null);

  // Diff dialog state
  const [diffTarget, setDiffTarget] = useState<{ versionA: number; versionB: number } | null>(null);
  const [diffResult, setDiffResult] = useState<AgentTemplateVersionDiff | null>(null);
  const [isDiffing, setIsDiffing] = useState(false);

  // Test + evaluation polling state
  const [testingVersionId, setTestingVersionId] = useState<number | null>(null);
  const [testedVersionId, setTestedVersionId] = useState<number | null>(null);
  const [testStartedAt, setTestStartedAt] = useState<number | null>(null);
  const [testTimedOut, setTestTimedOut] = useState(false);

  const versionsQuery = useQuery({
    ...listAgentTemplateVersionsOptions({ path: { templateId } }),
    enabled: templateId !== '',
  });
  const templateQuery = useQuery({
    ...getTemplateOptions({ path: { templateSlug: templateId } }),
    enabled: templateId !== '',
  });
  const evaluationsQuery = useQuery({
    ...getSubjectEvaluationsOptions({ path: { kind: 'agent_template', subjectId: templateId } }),
    enabled: templateId !== '',
    // Poll while a test is in flight, but never past the wall-clock cap — a stalled eval (e.g. a non-draining worker
    // fleet) must not poll forever.
    refetchInterval: () => {
      if (testingVersionId === null || testStartedAt === null) return false;
      if (Date.now() - testStartedAt > TEST_POLL_TIMEOUT_MS) return false;
      return 4000;
    },
  });

  // Stop polling when all assignments have a non-pending verdict.
  useEffect(() => {
    if (testingVersionId === null) return;
    if (computeVerdict(evaluationsQuery.data?.data ?? null) !== null) setTestingVersionId(null);
  }, [evaluationsQuery.data, testingVersionId]);

  // Hard cap: if the verdict never arrives, stop the poll and surface a "may still be running" state.
  useEffect(() => {
    if (testingVersionId === null || testStartedAt === null) return;
    const remaining = TEST_POLL_TIMEOUT_MS - (Date.now() - testStartedAt);
    const id = setTimeout(
      () => {
        setTestingVersionId(null);
        setTestTimedOut(true);
      },
      Math.max(0, remaining),
    );
    return () => clearTimeout(id);
  }, [testingVersionId, testStartedAt]);

  const versions = versionsQuery.data?.data ?? [];
  const activeVersion = versions.find((v) => v.status === 'active') ?? null;
  const template = templateQuery.data?.template ?? null;
  const evalData = evaluationsQuery.data?.data ?? null;

  const refreshVersions = () =>
    queryClient.invalidateQueries({ queryKey: listAgentTemplateVersionsQueryKey({ path: { templateId } }) });

  const createMutation = useMutation(createAgentTemplateVersionMutation());
  const promoteMutation = useMutation({
    ...promoteAgentTemplateVersionMutation(),
    onSuccess: () => {
      void refreshVersions();
      setPromoteError(null);
    },
    onError: (error: Error) => setPromoteError(error.message),
  });
  const rollbackMutation = useMutation({
    ...rollbackAgentTemplateVersionMutation(),
    onSuccess: () => void refreshVersions(),
    onError: (error: Error) => notify(error.message, 'danger'),
  });
  const testMutation = useMutation({
    ...testAgentTemplateVersionMutation(),
    onSuccess: (_result, variables) => {
      const versionId = Number(variables.path.versionId);
      setTestingVersionId(versionId);
      setTestedVersionId(versionId);
      setTestStartedAt(Date.now());
      setTestTimedOut(false);
      void queryClient.invalidateQueries({
        queryKey: getSubjectEvaluationsQueryKey({ path: { kind: 'agent_template', subjectId: templateId } }),
      });
    },
    onError: (error: Error) => notify(error.message, 'danger'),
  });
  const autoUpgradeMutation = useMutation({
    ...setTemplateAutoUpgradeMutation(),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: getTemplateQueryKey({ path: { templateSlug: templateId } }) }),
    onError: (error: Error) => notify(error.message, 'danger'),
  });

  const handleDiff = async (versionA: number, versionB: number) => {
    setDiffTarget({ versionA, versionB });
    setDiffResult(null);
    setIsDiffing(true);
    try {
      setDiffResult(
        await queryClient.fetchQuery(diffAgentTemplateVersionsOptions({ path: { templateId, versionA, versionB } })),
      );
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Could not compute the diff', 'danger');
    } finally {
      setIsDiffing(false);
    }
  };

  const getVersionVerdict = (versionId: number): TestVerdict => {
    if (testingVersionId === versionId) return 'evaluating';
    if (testedVersionId === versionId) return testTimedOut ? 'timed_out' : computeVerdict(evalData);
    return null;
  };

  const createForm = useZodForm(createVersionSchema, { defaultValues: { changeSummary: '' } });
  const closeCreate = () => {
    setShowCreate(false);
    createForm.reset();
  };

  return (
    <>
      <DetailPage
        title="Template versions"
        subtitle={template ? `Version history for ${template.title}` : 'Version history'}
        breadcrumbs={[
          { label: 'Libraries', to: '/agents/libraries' },
          { label: 'Templates', to: '/agents/templates' },
          ...(template ? [{ label: template.title, to: `/agents/templates/${template.slug}` }] : []),
        ]}
        actions={
          <Can policy="RequireSuperadmin">
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus size={14} aria-hidden="true" />
              Create version
            </Button>
          </Can>
        }
        alert={
          promoteError ? (
            <Callout tone="danger" title="Promote blocked">
              {promoteError}
            </Callout>
          ) : undefined
        }
      >
        <Can policy="CanManageAgentTemplates">
          <SettingList>
            <SettingRow
              title="Auto-promote marketplace versions"
              titleId="auto-promote-title"
              description="Automatically promote new versions received from the marketplace source."
            >
              <Toggle
                aria-label="Auto-promote marketplace versions"
                checked={template?.autoUpgrade ?? false}
                onChange={(value) =>
                  autoUpgradeMutation.mutate({ path: { templateSlug: templateId }, body: { autoUpgrade: value } })
                }
                disabled={autoUpgradeMutation.isPending || templateQuery.isPending}
              />
            </SettingRow>
          </SettingList>
        </Can>

        <QueryBoundary query={versionsQuery} label="versions" loading={<Skeleton height={96} />}>
          {() =>
            versions.length === 0 ? (
              <EmptyState
                icon={GitBranch}
                title="No versions yet"
                description="Create a version to start tracking changes to this template."
              />
            ) : (
              <div className="space-y-3">
                {versions.map((v) => (
                  <VersionCard
                    key={v.id}
                    version={v}
                    activeVersion={activeVersion}
                    verdict={getVersionVerdict(v.id)}
                    evalData={testedVersionId === v.id ? evalData : null}
                    onPromote={() => promoteMutation.mutate({ path: { templateId, versionId: String(v.id) } })}
                    onRollback={() => rollbackMutation.mutate({ path: { templateId, versionId: String(v.id) } })}
                    onDiff={() => activeVersion && handleDiff(v.version, activeVersion.version)}
                    onTest={() => testMutation.mutate({ path: { templateId, versionId: String(v.id) } })}
                    isPromoting={
                      promoteMutation.isPending && promoteMutation.variables?.path.versionId === String(v.id)
                    }
                    isRollingBack={
                      rollbackMutation.isPending && rollbackMutation.variables?.path.versionId === String(v.id)
                    }
                    isTesting={testMutation.isPending && testMutation.variables?.path.versionId === String(v.id)}
                  />
                ))}
              </div>
            )
          }
        </QueryBoundary>

        {versionsQuery.isSuccess && versions.length >= 2 && (
          <VersionComparePanel templateId={templateId} versions={versions} />
        )}
      </DetailPage>

      <Modal
        open={showCreate}
        onClose={closeCreate}
        title="Create version"
        description="Snapshot the template as a draft version."
      >
        <Form
          form={createForm}
          onSubmit={async (values) => {
            await createMutation.mutateAsync({
              path: { templateId },
              body: { configSnapshot: {}, changeSummary: values.changeSummary || undefined },
            });
            await refreshVersions();
            closeCreate();
            notify('Draft version created');
          }}
        >
          <TextAreaFormField
            name="changeSummary"
            label="Change summary"
            rows={3}
            placeholder="Describe what changed in this version"
          />
          <FormActions submitLabel="Create draft" onCancel={closeCreate} />
        </Form>
      </Modal>

      <Modal
        open={diffTarget !== null}
        onClose={() => {
          setDiffTarget(null);
          setDiffResult(null);
        }}
        title={
          diffTarget
            ? `Compare version ${diffTarget.versionA} with the active version ${diffTarget.versionB}`
            : 'Version diff'
        }
      >
        <div className="min-h-[120px]">
          {isDiffing && <Skeleton height={96} />}
          {!isDiffing && diffResult && <DiffPanel diff={diffResult} />}
          {!isDiffing && !diffResult && diffTarget && (
            <EmptyState
              icon={GitCompare}
              title="No differences returned"
              description="The server returned no diff for these versions."
            />
          )}
        </div>
      </Modal>
    </>
  );
};

export default TemplateVersionsPage;
